import type { ReportParameterValidValue } from '@/types';
import { canonicaliseSsrsDate } from './ssrsDate';

/**
 * Whether two parameter values name the same choice, tolerating incidental whitespace.
 *
 * Values are opaque ids as far as the portal is concerned — never parsed, never reformatted — but
 * PBIRS is not always byte-consistent about the id it hands back for what is, to a person, the
 * same choice: a subscription's stored `<Value>` comes from whatever wrote its XML, while a
 * resolved option's value comes fresh off the REST API on every lookup. PbirsDateTimeFormat
 * documents one already-known instance of the report server disagreeing with itself across its
 * two APIs (ISO vs day-first dates); trimming closes the same class of gap for everything else,
 * and can never turn one real choice into another — no genuine id depends on leading or trailing
 * whitespace to mean what it means.
 */
export function sameValue(a: string, b: string): boolean {
  return a === b || a.trim() === b.trim();
}

/** Whether `value` is present in `values`, tolerating the same incidental whitespace as sameValue. */
export function includesValue(values: string[], value: string): boolean {
  return values.some(v => sameValue(v, value));
}

/** Every entry matching `value` removed from `values` — the write side of includesValue. */
export function withoutValue(values: string[], value: string): string[] {
  return values.filter(v => !sameValue(v, value));
}

/**
 * The option a stored (or just-picked) value refers to, if the current list still offers it.
 *
 * This is the one place that decides whether a value counts as "known". The select, the checkbox
 * wall and the searchable picker all render their friendly label through this, so a value that
 * resolves here shows a name everywhere it appears, and one that does not shows the same
 * "no longer offered" wording everywhere — two call sites quietly deciding this differently is the
 * shape of bug this module exists to rule out.
 */
export function optionFor(
  value: string,
  options: ReportParameterValidValue[]
): ReportParameterValidValue | undefined {
  return options.find(o => sameValue(o.value, value));
}

/**
 * {@link optionFor}, plus the one stored spelling that means the same thing as an offered value
 * without being the same string: a date written in .NET's en-US general format.
 *
 * <b>Why this is separate from optionFor rather than folded into it.</b> Two spellings of one date
 * live in dbo.Subscriptions.Parameters — the day-first one SSRS writes, which is what the resolver's
 * options use, and the en-US one. Measured on MANDW01: of the 17 `/Sales/Scope 3` subscriptions
 * storing a ScopeVersion, 9 hold `01/01/1900 00:00:00` and 8 hold `1/1/1900 12:00:00 AM`. Same
 * instant, same nine offered values — and those 8 were the ones reading "(no longer offered)"
 * against a version the report still offers.
 *
 * Teaching `optionFor` itself to bridge that gap breaks the controls it also serves. The
 * multi-valued checkbox wall decides `checked` with `includesValue` and unticks with
 * `withoutValue(values, option.value)` — both compare against what is STORED, so a wall whose
 * matching was generous but whose removal was exact would render a value it could not then untick:
 * invisible as an unmatched box, unreachable through the real one. Being generous about what a
 * value MEANS is only safe where nothing is removed by it, which is the single-valued select and
 * nowhere else. Removing stays exact everywhere.
 */
export function selectedOptionFor(
  value: string,
  options: ReportParameterValidValue[]
): ReportParameterValidValue | undefined {
  const direct = optionFor(value, options);
  if (direct) return direct;

  const canonical = canonicaliseSsrsDate(value);
  return canonical === value ? undefined : optionFor(canonical, options);
}

/**
 * The wording shown for a stored value the current list does not recognise.
 *
 * Never used to drop or change the value itself — see ParameterField and SearchablePicker, which
 * both keep it selected and removable. It says only that this particular answer did not come back
 * from the list just resolved, not that it is wrong: the report may no longer offer it, or an
 * upstream choice may not (yet) agree with what is stored. Matches the wording the single-valued
 * select has used for this since the cascade work began, so the same fact reads the same way
 * whichever control a parameter happens to render as.
 *
 * One reason it must NOT cover any more is "the list was too long to send". That case was
 * indistinguishable from a retired value and far more common: Head Office on /Sales/Scope 3
 * resolves 5,846 entries, arrives capped at 500, and subscription 6cf6e03f's perfectly current
 * 199159 sits past the cut — so the editor described a live head office as retired and offered to
 * take it off. The server now pins the caller's own selections into its answer whatever the cap and
 * whatever the search (see ResolveParameterValuesRequest.selected), which is what makes the two
 * tellable apart. A client-side fix was never available: the entry did not reach the browser at all.
 */
export function unmatchedLabel(value: string): string {
  return `${value} (no longer offered)`;
}
